import { useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sparkles, Upload, Image as ImageIcon, FileText, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const REQUIRED = ["customer_name", "customer_phone", "customer_email"];
const COLS = [
  ["customer_name", "Name"], ["customer_company", "Company"], ["customer_phone", "Mobile"],
  ["customer_email", "Email"], ["origin", "Origin"], ["destination", "Destination"],
];

export default function SmartImportDialog({ onImported }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setText(""); setFiles([]); setRows([]); if (fileRef.current) fileRef.current.value = ""; };
  const addFiles = (list) => setFiles((prev) => [...prev, ...Array.from(list)]);
  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); };

  const parse = async () => {
    if (!text.trim() && !files.length) return toast.error("Paste some text or add files/images first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", text);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/leads/smart-import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parsed = (data.leads || []).map((l) => ({ ...l, items: l.items || [] }));
      setRows(parsed);
      if (!parsed.length) toast.error("AI couldn't find any leads — try clearer text or a sharper image");
      else toast.success(`AI extracted ${parsed.length} lead(s)`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const setCell = (i, k, v) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const createLeads = async () => {
    const valid = rows.filter((r) => (r.customer_name || "").trim() || (r.customer_phone || "").trim());
    if (!valid.length) return toast.error("Nothing to create — fill in a name or mobile");
    setBusy(true);
    try {
      const payload = valid.map(({ image_preview, ...r }) => ({ ...r, article_value: parseFloat(r.article_value) || 0 }));
      const { data } = await api.post("/leads/import/save", { leads: payload });
      toast.success(`Created ${data.created} lead(s) — Packing Schedule auto-populated`);
      setOpen(false); reset(); onImported?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button className="rounded-none gap-2 bg-primary" onClick={() => setOpen(true)} data-testid="import-smart-leads-btn">
        <Sparkles className="h-4 w-4" /> Import Smart Leads
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-5xl rounded-none max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle className="font-heading text-2xl flex items-center gap-2"><Wand2 className="h-6 w-6 text-primary" /> Smart Import — AI Lead Extraction</DialogTitle></DialogHeader>

          <div className="overflow-y-auto flex-1 space-y-4 pr-1">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Paste email / raw text</Label>
                <Textarea rows={7} className="rounded-none bg-card text-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste the enquiry email or any unstructured text here…" data-testid="smart-text" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Upload Word files &amp; images</Label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={onDrop}
                  className={`border-2 border-dashed rounded-none h-[168px] flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${drag ? "border-primary bg-primary/5" : "border-border"}`}
                  onClick={() => fileRef.current?.click()}
                  data-testid="smart-dropzone"
                >
                  <Upload className="h-6 w-6 text-primary" />
                  <div className="text-sm text-muted-foreground">Drag &amp; drop multiple files, or click to browse</div>
                  <div className="text-[11px] text-muted-foreground">.docx / .doc · .png / .jpg / .webp</div>
                  <input ref={fileRef} type="file" multiple accept=".docx,.doc,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => e.target.files && addFiles(e.target.files)} data-testid="smart-file-input" />
                </div>
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {files.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[11px] border border-border px-2 py-1 bg-muted" data-testid={`smart-file-${i}`}>
                        {/(png|jpg|jpeg|webp)$/i.test(f.name) ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                        {f.name}
                        <button onClick={(e) => { e.stopPropagation(); setFiles((p) => p.filter((_, idx) => idx !== i)); }} className="text-destructive ml-1">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button className="rounded-none gap-2 font-bold uppercase tracking-wide" onClick={parse} disabled={busy} data-testid="smart-parse-btn">
              <Sparkles className="h-4 w-4" /> {busy ? "AI is reading…" : "Extract with AI"}
            </Button>

            {rows.length > 0 && (
              <div className="space-y-4" data-testid="smart-draft">
                <div className="text-xs text-muted-foreground">Review the draft below. <span className="text-destructive font-bold">Red</span> fields are missing — fill them in before creating.</div>
                {rows.map((r, i) => (
                  <div key={i} className="border border-border p-3 space-y-3" data-testid={`smart-lead-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-heading uppercase tracking-wide text-muted-foreground">Lead #{i + 1}{r.duplicate_phone && <span className="ml-2 inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" data-testid={`smart-dupe-${i}`} title="This phone already exists in the pipeline">Duplicate phone</span>}</span>
                      <Button size="sm" variant="ghost" className="rounded-none h-7 text-destructive gap-1 text-xs" onClick={() => removeRow(i)} data-testid={`smart-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /> Remove</Button>
                    </div>
                    <div className="grid md:grid-cols-[1fr_auto] gap-4">
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                        {COLS.map(([k, lbl]) => {
                          const missing = REQUIRED.includes(k) && !String(r[k] || "").trim();
                          return (
                            <div key={k} className="space-y-1">
                              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{lbl}{missing && <span className="text-destructive"> *</span>}</Label>
                              <Input className={`rounded-none h-8 bg-card text-xs ${missing ? "border-destructive ring-1 ring-destructive" : ""}`} value={r[k] || ""} onChange={(e) => setCell(i, k, e.target.value)} data-testid={`smart-${i}-${k}`} />
                            </div>
                          );
                        })}
                        <div className="space-y-1">
                          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Article Value (₹)</Label>
                          <Input type="number" min="0" placeholder="Optional" className="rounded-none h-8 bg-card text-xs" value={r.article_value ?? ""} onChange={(e) => setCell(i, "article_value", e.target.value)} data-testid={`smart-${i}-article_value`} />
                        </div>
                        <div className="col-span-2 lg:col-span-3">
                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Inventory ({r.items?.length || 0})</div>
                          <div className="flex flex-wrap gap-1.5">
                            {(r.items || []).map((it, j) => <span key={j} className="text-[11px] border border-border px-2 py-0.5 bg-muted">{it.name} ×{it.quantity}</span>)}
                            {!(r.items || []).length && <span className="text-[11px] text-destructive">No articles detected</span>}
                          </div>
                        </div>
                      </div>
                      {r.image_preview && (
                        <div className="w-56 shrink-0">
                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Original Image</div>
                          <img src={r.image_preview} alt="source" className="w-56 max-h-64 object-contain border border-border" data-testid={`smart-image-${i}`} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div className="flex justify-end border-t border-border pt-2 text-sm" data-testid="smart-total-article-value">
                  Total Article Value: <b className="ml-2 font-mono-num">₹{rows.reduce((s, r) => s + (parseFloat(r.article_value) || 0), 0).toLocaleString("en-IN")}</b>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="ghost" className="rounded-none" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
            <Button className="rounded-none gap-2 font-bold uppercase tracking-wide" onClick={createLeads} disabled={busy || !rows.length} data-testid="smart-create-btn">
              <Upload className="h-4 w-4" /> Create Lead{rows.length !== 1 ? "s" : ""} ({rows.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
